// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import React, { ReactElement } from 'react';
import { AlertBulkEditForm, AlertBulkEditValues } from './AlertBulkEditForm';
import { CustomStatusMapType } from 'thehive-sdk';
import { trimTags } from '@Helpers/tags';

interface AlertBulkEditManageProps {
    triggerElem: ReactElement;
    changeStatus?: boolean;
    statusList?: CustomStatusMapType;
    onEdit: (values: AlertBulkEditValues) => void;
}

export const AlertBulkEditManage = ({
    triggerElem,
    changeStatus = false,
    statusList = {},
    onEdit,
}: AlertBulkEditManageProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = (values: AlertBulkEditValues): void => {
        const valuesProcessed: any = Object.fromEntries(
            Object.entries(values).filter(([key]) => {
                return !key.includes('Checked') && values[`${key}Checked`];
            }),
        );

        onEdit({
            ...valuesProcessed,
            addTags: trimTags(valuesProcessed.addTags || []),
            removeTags: trimTags(valuesProcessed.removeTags || []),
        });

        form.resetFields();
        setFieldsTouched(false);
    };

    return (
        <THDrawer
            title={i('alert.bulk-edit.drawer.title')}
            triggerElem={triggerElem}
            afterVisibleChange={() => form.resetFields()}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <AlertBulkEditForm
                    onFinish={(values) => {
                        onFinish(values);
                        toggleDrawer();
                    }}
                    form={form}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    isValid={isValid && fieldsTouched}
                    changeStatus={changeStatus}
                    statusList={statusList}
                    onCancel={() => {
                        form.resetFields();
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                />
            )}
        </THDrawer>
    );
};
